import React, { useState, useEffect, useRef } from "react";
import { redirectToOAuth } from "../../../utils/safeUrl";
import axios from "axios";
import { Trash2, Plus, Pencil, ChevronDown, ArrowRight } from "lucide-react";
import EmailTestModal from "./EmailTestModal";
import EmailBodyInput from "./EmailBodyInput";
import QuestionsBuilder, { cleanQuestions, defaultActiveQuestions, firstMissingOptions, firstEmptyLabel, LOCATION_QUESTION_KEY, QuestionSummaryRow } from "../screening/QuestionsBuilder";
import whatsappLogo from "../../assets/whatsapp.svg";

const SOURCING_URL = process.env.REACT_APP_SOURCING_API_URL;

const FORM_LINK_TOKEN = "{{form_link}}";

const VARIABLES = [
    { label: "Candidate Name", token: "{{candidate_name}}" },
    { label: "Role",           token: "{{candidate_role}}" },
    { label: "Company",        token: "{{candidate_company}}" },
    { label: "Location",       token: "{{candidate_location}}" },
    { label: "Screening Form Link", token: "{{form_link}}" },
];

const BASE_CHANNELS = [
    { value: "email", label: "Email" },
];
// Offered only on screening sequences — a call step's questions come from the screening form,
// so there's nothing for the agent to ask without one (enforced again in validation on save).
const CALL_CHANNEL = { value: "call", label: "Call" };

const hasEmail = (ch) => ch === "email" || ch === "both";
const hasWhatsApp = (ch) => ch === "whatsapp" || ch === "both";
const hasCall = (ch) => ch === "call";
const channelLabel = (ch) =>
    ch === "call" ? "Call" : ch === "both" ? "Email + WhatsApp" : ch === "whatsapp" ? "WhatsApp" : "Email";

// Render a WhatsApp template body ({{1}}, {{2}}…) into a readable preview using its param
// mapping — candidate tokens become friendly labels; static text stays as-is.
const VARIABLE_LABELS = {
    "{{candidate_name}}": "Candidate Name",
    "{{candidate_first_name}}": "First Name",
    "{{candidate_role}}": "Role",
    "{{candidate_company}}": "Company",
    "{{candidate_location}}": "Location",
};
const renderWaPreview = (tmpl) => {
    if (!tmpl) return "";
    return (tmpl.body || "").replace(/\{\{(\d+)\}\}/g, (m, n) => {
        const param = (tmpl.params || [])[Number(n) - 1];
        if (!param) return m;
        return VARIABLE_LABELS[param] || param;
    });
};

const blankStep = (template) => ({
    channel: "email",
    subject: template?.subject || "",
    body: template?.body || "",
    whatsappTemplateId: null,
    delayDays: 3,
    sendAt: "09:00",
});

const SequenceCreatorModal = ({
    isOpen, onClose, globalTemplate, orgId, recruiterId, onSave,
    editMode = false,
    editSequence = null,
    editSteps = [],
}) => {
    const [seqName, setSeqName] = useState("Untitled Outreach");
    const [isEditingName, setIsEditingName] = useState(false);
    const [activeIdx, setActiveIdx] = useState(0);
    const [startTiming, setStartTiming] = useState("immediately");
    const [steps, setSteps] = useState([]);
    const [gmailEmail, setGmailEmail] = useState(null);
    const [accountLoading, setAccountLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [previewStep, setPreviewStep] = useState(null);
    const [dirty, setDirty] = useState(false);
    const [seqType, setSeqType] = useState("plain");
    const [questions, setQuestions] = useState([]);
    const [formId, setFormId] = useState(null);
    const [waTemplates, setWaTemplates] = useState([]);
    const [showFormBuilder, setShowFormBuilder] = useState(false);
    const [currency, setCurrency] = useState("INR");

    const stepRefs = useRef([]);
    const screeningRef = useRef(null);
    const focusedStepIdxRef = useRef(0);
    const focusedFieldRef = useRef("body");
    const subjectRefs = useRef([]);
    const bodyRefs = useRef([]);
    const originalStepIdsRef = useRef(new Set());
    // Snapshot of questions/currency taken when the form builder opens, so Cancel can revert.
    const formBuilderSnapshotRef = useRef(null);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        if (!isOpen) return;
        setIsEditingName(false);
        setActiveIdx(0);
        setStartTiming("immediately");
        setError(null);
        setDirty(false);
        stepRefs.current = [];
        subjectRefs.current = [];
        bodyRefs.current = [];
        setSeqType(editMode && editSequence ? (editSequence.type || "plain") : "plain");
        setQuestions([]);
        setFormId(null);
        setShowFormBuilder(false);
        formBuilderSnapshotRef.current = null;
        setCurrency("INR");
        if (editMode && editSequence) {
            setSeqName(editSequence.name);
            setSteps(editSteps);
            originalStepIdsRef.current = new Set(editSteps.map(s => s._stepId).filter(Boolean));
            // Load the attached screening form's questions for editing.
            if (editSequence.type === "screening") {
                axios
                    .get(`${SOURCING_URL}/email/sequences/${editSequence.id}/form`)
                    .then(({ data }) => {
                        if (data.form) {
                            setFormId(data.form.id);
                            setQuestions((data.form.questions || []).map(q => ({ options: [], ...q })));
                            setCurrency(data.form.currency || "INR");
                        }
                    })
                    .catch(() => { });
            }
        } else {
            setSeqName("Untitled Outreach");
            setSteps([
                blankStep(globalTemplate),
                { ...blankStep(globalTemplate), delayDays: 3 },
                { ...blankStep(globalTemplate), delayDays: 4 },
            ]);
            originalStepIdsRef.current = new Set();
        }
        focusedStepIdxRef.current = 0;
        focusedFieldRef.current = "body";
    }, [isOpen]); // intentional: snapshot globalTemplate at open time

    useEffect(() => {
        if (!isOpen || !recruiterId || !orgId) return;
        setAccountLoading(true);
        axios
            .get(`${SOURCING_URL}/auth/email-account`, { params: { created_by: recruiterId, org_id: orgId } })
            .then(({ data }) => setGmailEmail(data.connected ? data.email : null))
            .catch(() => setGmailEmail(null))
            .finally(() => setAccountLoading(false));
    }, [isOpen, recruiterId, orgId]);

    // Registered (dev-seeded) WhatsApp templates the recruiter can pick per step.
    useEffect(() => {
        if (!isOpen) return;
        axios
            .get(`${SOURCING_URL}/whatsapp/templates`)
            .then(({ data }) => setWaTemplates(data))
            .catch(() => setWaTemplates([]));
    }, [isOpen]);

    const updateStep = (idx, field, value) => {
        setDirty(true);
        setSteps(prev => {
            const updated = prev.map((s, i) => i === idx ? { ...s, [field]: value } : s);
            if (field === "delayDays") {
                for (let i = idx + 1; i < updated.length; i++) {
                    if (updated[i].delayDays <= updated[i - 1].delayDays) {
                        updated[i] = { ...updated[i], delayDays: updated[i - 1].delayDays + 1 };
                    }
                }
            }
            return updated;
        });
    };

    const addStep = () => {
        setDirty(true);
        setSteps(prev => {
            const lastDelay = prev.length > 0 ? prev[prev.length - 1].delayDays : 0;
            return [...prev, { ...blankStep(globalTemplate), delayDays: Math.max(3, lastDelay + 1) }];
        });
        const newIdx = steps.length;
        setActiveIdx(newIdx);
        setTimeout(() => {
            stepRefs.current[newIdx]?.scrollIntoView({ behavior: "smooth", block: "start" });
        }, 50);
    };

    const deleteStep = (idx) => {
        const msg = editMode
            ? "Remove this step?\n\nCandidates already in this outreach will still receive mail for steps already sent to them."
            : "Remove this step?";
        if (!window.confirm(msg)) return;
        setDirty(true);
        const updated = steps.filter((_, i) => i !== idx);
        setSteps(updated);
        setActiveIdx(prev => {
            if (idx < prev) return prev - 1;
            if (idx === prev) return Math.min(prev, updated.length - 1);
            return prev;
        });
    };

    const handleStepClick = (idx) => {
        setActiveIdx(idx);
        stepRefs.current[idx]?.scrollIntoView({ behavior: "smooth", block: "start" });
    };

    const insertSnippet = (stepIdx, token) => {
        const isSubject = focusedFieldRef.current === "subject";
        if (isSubject) {
            const el = subjectRefs.current[stepIdx];
            if (!el) return;
            const start = el.selectionStart ?? el.value.length;
            const end = el.selectionEnd ?? el.value.length;
            const current = steps[stepIdx]?.subject || "";
            updateStep(stepIdx, "subject", current.slice(0, start) + token + current.slice(end));
            setTimeout(() => {
                el.focus();
                el.setSelectionRange(start + token.length, start + token.length);
            }, 0);
        } else {
            const quill = bodyRefs.current[stepIdx]?.getEditor();
            if (!quill) return;
            const range = quill.getSelection(true);
            const index = range ? range.index : quill.getLength();
            quill.insertText(index, token, 'user');
            quill.setSelection(index + token.length, 0);
        }
    };

    // Ensure the screening link token is present in the first step's body.
    const ensureFormLink = () => {
        setSteps(prev => {
            if (prev.some(s => (s.body || "").includes(FORM_LINK_TOKEN))) return prev;
            return prev.map((s, i) =>
                i === 0
                    ? { ...s, body: `${s.body || ""}<p>Fill out a few quick questions: ${FORM_LINK_TOKEN}</p>` }
                    : s
            );
        });
    };

    const handleTypeChange = (value) => {
        setDirty(true);
        setSeqType(value);
        if (value === "screening") {
            if (questions.length === 0) {
                // Predefined questions live in the library; seed only the required defaults.
                setQuestions(defaultActiveQuestions());
            }
            ensureFormLink();
        } else {
            // Call steps need a screening form — leaving screening invalidates them.
            setSteps(prev => prev.map(s => hasCall(s.channel) ? { ...s, channel: "email" } : s));
        }
    };

    const includeScreening = seqType === "screening";
    const toggleScreening = (checked) => handleTypeChange(checked ? "screening" : "plain");

    const scrollToScreening = () => {
        setActiveIdx(-1);
        screeningRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    };

    const handleCancel = () => {
        if (!dirty || window.confirm("Discard your unsaved changes to this outreach?")) onClose();
    };

    const openFormBuilder = () => {
        formBuilderSnapshotRef.current = { questions, currency };
        setShowFormBuilder(true);
    };

    const cancelFormBuilder = () => {
        const snap = formBuilderSnapshotRef.current;
        const changed = snap && JSON.stringify({ questions, currency }) !== JSON.stringify(snap);
        if (changed && !window.confirm("Discard your changes to the screening form?")) return;
        if (snap) { setQuestions(snap.questions); setCurrency(snap.currency); }
        setShowFormBuilder(false);
    };

    const saveFormBuilder = () => {
        const empty = firstEmptyLabel(questions);
        if (empty) {
            alert("Fill in the question text before saving (or remove the empty question).");
            return;
        }
        const missingOptions = firstMissingOptions(cleanQuestions(questions));
        if (missingOptions) {
            alert(`Add at least one option to "${missingOptions.label}" before saving.`);
            return;
        }
        setShowFormBuilder(false);
    };

    const handleConnectMailbox = async () => {
        try {
            const { data } = await axios.get(`${SOURCING_URL}/auth/gmail/connect`, {
                params: { created_by: recruiterId, org_id: orgId },
            });
            redirectToOAuth(data.auth_url);
        } catch {
            setError("Failed to start Gmail connection. Please try again.");
        }
    };

    const handleSave = async () => {
        if (!seqName.trim()) { setError("Outreach name is required."); return; }
        setSaving(true);
        setError(null);
        try {
            if (editMode) {
                await handleEditSave();
            } else {
                await handleCreateSave();
            }
        } catch (e) {
            setError(e.response?.data?.detail || e.message || "Failed to save outreach.");
        } finally {
            setSaving(false);
        }
    };

    const validateSteps = () => {
        for (let i = 0; i < steps.length; i++) {
            if (hasWhatsApp(steps[i].channel) && !steps[i].whatsappTemplateId) {
                throw new Error(`Step ${i + 1}: pick a WhatsApp template (or change the channel).`);
            }
            if (hasCall(steps[i].channel) && seqType !== "screening") {
                throw new Error(`Step ${i + 1}: Call steps need a Screening outreach (to ask questions).`);
            }
        }
        // Screening sequences deliver the form via {{form_link}} in an email — the token is auto-added
        // on toggle but can be edited out, so require it in an email step before saving.
        if (seqType === "screening") {
            const emailSteps = steps.filter(s => hasEmail(s.channel));
            if (emailSteps.length > 0 && !emailSteps.some(
                s => (s.body || "").includes(FORM_LINK_TOKEN) || (s.subject || "").includes(FORM_LINK_TOKEN)
            )) {
                throw new Error(`Add ${FORM_LINK_TOKEN} to an email step so candidates can reach the screening form.`);
            }
        }
    };

    // Create an email template (if the step uses email) and the step row referencing
    // whichever channels apply. Used by create flow and edit-recreate.
    const createStepOnServer = async (seqId, step, stepNumber, delayDays) => {
        let templateId = null;
        if (hasEmail(step.channel)) {
            const { data: tmpl } = await axios.post(`${SOURCING_URL}/email/templates`, {
                org_id: orgId, created_by: recruiterId,
                name: `${seqName} – Email ${stepNumber}`,
                subject: step.subject, body: step.body,
            });
            templateId = tmpl.id;
        }
        await axios.post(`${SOURCING_URL}/email/sequences/${seqId}/steps`, {
            step_number: stepNumber,
            delay_days: delayDays,
            ...(templateId ? { template_id: templateId } : {}),
            ...(hasWhatsApp(step.channel) ? { whatsapp_template_id: step.whatsappTemplateId } : {}),
            ...(hasCall(step.channel) ? { is_call_step: true } : {}),
        });
    };

    const handleCreateSave = async () => {
        const isScreening = seqType === "screening";
        const cleaned = isScreening ? cleanQuestions(questions) : null;
        if (isScreening && cleaned.length === 0) {
            throw new Error("Add at least one screening question.");
        }
        if (isScreening) {
            const missingOptions = firstMissingOptions(cleaned);
            if (missingOptions) throw new Error(`Add at least one option to "${missingOptions.label}" before saving.`);
        }
        validateSteps();
        const hasCTC = isScreening && questions.some(q => q.key === "expected_ctc" || q.key === "current_ctc");
        const locations = isScreening ? (questions.find(q => q.key === LOCATION_QUESTION_KEY)?.options || []).filter(Boolean) : [];
        const { data: seq } = await axios.post(`${SOURCING_URL}/email/sequences`, {
            org_id: orgId, created_by: recruiterId, name: seqName,
            type: seqType, questions: cleaned,
            ...(isScreening ? { currency: hasCTC ? currency : null, locations } : {}),
        });
        for (let i = 0; i < steps.length; i++) {
            await createStepOnServer(seq.id, steps[i], i + 1, i === 0 ? 0 : steps[i].delayDays);
        }
        onSave(seq);
    };

    const handleEditSave = async () => {
        const seqId = editSequence.id;
        validateSteps();
        if (seqName !== editSequence.name) {
            await axios.put(`${SOURCING_URL}/email/sequences/${seqId}`, { name: seqName });
        }
        // Update the attached screening form's questions, if any.
        if (seqType === "screening" && formId) {
            const cleaned = cleanQuestions(questions);
            const missingOptions = firstMissingOptions(cleaned);
            if (missingOptions) throw new Error(`Add at least one option to "${missingOptions.label}" before saving.`);
            const hasCTC = questions.some(q => q.key === "expected_ctc" || q.key === "current_ctc");
            const locations = (questions.find(q => q.key === LOCATION_QUESTION_KEY)?.options || []).filter(Boolean);
            await axios.put(`${SOURCING_URL}/engagement/forms/${formId}`, {
                questions: cleaned,
                currency: hasCTC ? currency : null,
                locations,
            });
        }
        // Delete steps that were removed
        const finalStepIds = new Set(steps.map(s => s._stepId).filter(Boolean));
        for (const oldId of originalStepIdsRef.current) {
            if (!finalStepIds.has(oldId)) {
                await axios.delete(`${SOURCING_URL}/email/sequences/${seqId}/steps/${oldId}`);
            }
        }
        // Update existing steps or create new ones
        for (let i = 0; i < steps.length; i++) {
            const step = steps[i];
            const stepNumber = i + 1;
            const delayDays = i === 0 ? 0 : step.delayDays;

            if (!step._stepId) {
                await createStepOnServer(seqId, step, stepNumber, delayDays);
                continue;
            }

            // The step PUT can't null a field, so if a channel was removed from an existing
            // step — or the step flipped to/from a call step — recreate it (delete + create) to
            // drop the stale template/flag reference.
            const removedEmail = step._templateId && !hasEmail(step.channel);
            const removedWa = step._whatsappTemplateId && !hasWhatsApp(step.channel);
            const callToggled = !!step._isCallStep !== hasCall(step.channel);
            if (removedEmail || removedWa || callToggled) {
                await axios.delete(`${SOURCING_URL}/email/sequences/${seqId}/steps/${step._stepId}`);
                await createStepOnServer(seqId, step, stepNumber, delayDays);
                continue;
            }

            // Update in place.
            let templateId = step._templateId;
            if (hasEmail(step.channel)) {
                if (templateId) {
                    await axios.put(`${SOURCING_URL}/email/templates/${templateId}`, {
                        subject: step.subject, body: step.body,
                    });
                } else {
                    const { data: tmpl } = await axios.post(`${SOURCING_URL}/email/templates`, {
                        org_id: orgId, created_by: recruiterId,
                        name: `${seqName} – Email ${stepNumber}`,
                        subject: step.subject, body: step.body,
                    });
                    templateId = tmpl.id;
                }
            }
            await axios.put(`${SOURCING_URL}/email/sequences/${seqId}/steps/${step._stepId}`, {
                step_number: stepNumber,
                delay_days: delayDays,
                ...(hasEmail(step.channel) ? { template_id: templateId } : {}),
                ...(hasWhatsApp(step.channel) ? { whatsapp_template_id: step.whatsappTemplateId } : {}),
            });
        }
        onSave({ ...editSequence, name: seqName });
    };

    if (!isOpen || steps.length === 0) return null;

    const channelOptions = seqType === "screening" ? [...BASE_CHANNELS, CALL_CHANNEL] : BASE_CHANNELS;

    const today = new Date().toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });

    return (
        <>
            <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center">
                <div className="w-[92vw] h-[90vh] bg-white dark:bg-[#1B1524] rounded-2xl flex flex-col overflow-hidden shadow-2xl">

                    {/* Header */}
                    <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 dark:border-gray-700/50 shrink-0">
                        <h2 className="text-base font-semibold text-gray-800 dark:text-white">{editMode ? "Edit outreach" : "Create outreach"}</h2>
                        <div className="flex items-center gap-2">
                            <button
                                onClick={handleCancel}
                                className="px-4 py-1.5 text-sm border border-gray-200 dark:border-gray-600 rounded-full text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-white/5"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleSave}
                                disabled={saving}
                                className="px-5 py-1.5 text-sm font-medium text-white rounded-full disabled:opacity-50"
                                style={{ background: "linear-gradient(to right, #ec4899, #3b82f6)" }}
                            >
                                {saving ? (editMode ? "Updating…" : "Saving…") : (editMode ? "Update" : "Save")}
                            </button>
                        </div>
                    </div>

                    {/* Sub-header */}
                    <div className="flex items-center gap-3 px-6 py-3 border-b border-gray-100 dark:border-gray-700/50 bg-gray-50 dark:bg-white/[0.03] shrink-0">
                        <div className="flex items-center gap-1.5 flex-1 min-w-0">
                            {isEditingName ? (
                                <input
                                    value={seqName}
                                    onChange={e => { setDirty(true); setSeqName(e.target.value); }}
                                    onBlur={() => setIsEditingName(false)}
                                    onKeyDown={e => { if (e.key === "Enter") setIsEditingName(false); }}
                                    className="text-sm font-semibold text-gray-800 dark:text-white border-b border-purple-400 outline-none bg-transparent"
                                    autoFocus
                                />
                            ) : (
                                <button
                                    onClick={() => setIsEditingName(true)}
                                    className="text-sm font-semibold text-gray-800 dark:text-white hover:text-purple-600 flex items-center gap-1 min-w-0"
                                >
                                    <span className="truncate">{seqName}</span>
                                    <Pencil className="w-3.5 h-3.5 text-gray-400 dark:text-gray-500 shrink-0" />
                                </button>
                            )}
                        </div>
                        <p className="text-xs text-gray-400 dark:text-gray-500 shrink-0">
                            Created by {gmailEmail || recruiterId} · {today}
                        </p>
                    </div>

                    {/* Include Screening toggle */}
                    <div className="flex items-center gap-2 px-6 py-2.5 border-b border-gray-100 dark:border-gray-700/50 shrink-0">
                        <label className={`flex items-center gap-2 select-none ${editMode ? "cursor-default" : "cursor-pointer"}`}>
                            <input
                                type="checkbox"
                                checked={includeScreening}
                                disabled={editMode}
                                onChange={e => toggleScreening(e.target.checked)}
                                className="w-4 h-4 rounded border-gray-300 dark:border-gray-600 text-purple-600 dark:text-purple-300 focus:ring-purple-400 disabled:opacity-50"
                            />
                            <span className="text-xs font-medium text-gray-600 dark:text-gray-300">Include Screening</span>
                        </label>
                        {includeScreening && (
                            <span className="text-[11px] text-gray-400 dark:text-gray-500 ml-2">
                                Adds Step 1 (screening form). Insert <code className="text-purple-600 dark:text-purple-300">{FORM_LINK_TOKEN}</code> in your email — candidates get a unique form link.
                            </span>
                        )}
                    </div>

                    {error && <p className="text-xs text-red-500 dark:text-red-400 px-6 py-1.5 bg-red-50 dark:bg-red-500/10 shrink-0">{error}</p>}

                    {/* Body */}
                    <div className="flex flex-1 overflow-hidden">

                        {/* Left panel */}
                        <div className="w-72 border-r border-gray-100 dark:border-gray-700/50 flex flex-col bg-white dark:bg-white/5 shrink-0">
                            {steps.length < 3 && (
                                <div className="px-3 py-2 border-b border-gray-100 dark:border-gray-700/50 shrink-0">
                                    <div className="bg-amber-50 dark:bg-amber-500/10 border border-amber-200 dark:border-amber-500/40 rounded-lg px-3 py-2">
                                        <p className="text-xs text-amber-700 dark:text-amber-300 leading-snug">
                                            ⚠ We recommend at least having 3 steps in your outreach
                                        </p>
                                    </div>
                                </div>
                            )}
                            <div className="flex-1 overflow-y-auto">
                                {includeScreening && (
                                    <>
                                        {/* Step 1 — Screening Form */}
                                        <button
                                            onClick={scrollToScreening}
                                            className={`w-full text-left px-4 py-3 border-b border-gray-50 transition-all ${activeIdx === -1
                                                    ? "bg-purple-50 border-l-2 border-l-purple-500"
                                                    : "hover:bg-gray-50"
                                                }`}
                                        >
                                            <p className={`text-xs font-semibold truncate ${activeIdx === -1 ? "text-purple-700" : "text-gray-700"}`}>
                                                Step 1: Screening Form
                                            </p>
                                            <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
                                                {questions.length} question{questions.length === 1 ? "" : "s"}
                                            </p>
                                        </button>
                                        {/* Step 2 — Sequence group label */}
                                        <div className="px-4 pt-3 pb-1">
                                            <p className="text-xs font-semibold text-gray-700 dark:text-gray-300">Step 2: Outreach</p>
                                        </div>
                                    </>
                                )}
                                {steps.map((step, idx) => (
                                    <button
                                        key={idx}
                                        onClick={() => handleStepClick(idx)}
                                        className={`w-full text-left px-4 py-3 border-b border-gray-50 transition-all group relative ${includeScreening ? "pl-6" : ""
                                            } ${activeIdx === idx
                                                ? "bg-purple-50 border-l-2 border-l-purple-500"
                                                : "hover:bg-gray-50"
                                            }`}
                                    >
                                        <div className="flex items-start justify-between">
                                            <div className="flex-1 min-w-0 pr-1">
                                                <p className={`text-xs font-semibold truncate ${activeIdx === idx ? "text-purple-700" : "text-gray-700"}`}>
                                                    {includeScreening ? `Step 2.${idx + 1}` : `Step ${idx + 1}`}: {channelLabel(step.channel)}
                                                </p>
                                                {hasEmail(step.channel) && gmailEmail && (
                                                    <p className="text-xs text-gray-400 dark:text-gray-500 truncate mt-0.5">{gmailEmail}</p>
                                                )}
                                                <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
                                                    {idx === 0
                                                        ? (startTiming === "immediately" ? "Immediately" : "Next day")
                                                        : `+${step.delayDays} days`}
                                                </p>
                                            </div>
                                            {steps.length > 1 && (
                                                <button
                                                    onClick={e => { e.stopPropagation(); deleteStep(idx); }}
                                                    className="opacity-0 group-hover:opacity-100 text-gray-300 dark:text-gray-600 hover:text-red-400 transition-all shrink-0 mt-0.5"
                                                >
                                                    <Trash2 className="w-3.5 h-3.5" />
                                                </button>
                                            )}
                                        </div>
                                    </button>
                                ))}

                                {/* Add step — right below last step, scrolls with list */}
                                <button
                                    onClick={addStep}
                                    className="flex items-center justify-center gap-1.5 w-full py-3 text-xs text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-white/10 transition-all border-b border-gray-50"
                                >
                                    <Plus className="w-3.5 h-3.5" />
                                    Add step
                                </button>
                            </div>

                        </div>

                        {/* Right panel — all steps stacked */}
                        <div className="flex-1 overflow-y-auto px-6 py-5">

                            {!accountLoading && !gmailEmail && (
                                <div className="max-w-2xl w-full mx-auto mb-4 flex items-center justify-between gap-3 px-4 py-3 bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/30 rounded-xl">
                                    <div className="flex items-center gap-2 text-sm text-red-700 dark:text-red-300">
                                        <svg className="w-4 h-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                                d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
                                        </svg>
                                        Missing sender email. Connect an email account to start sending.
                                    </div>
                                    <button
                                        onClick={handleConnectMailbox}
                                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium text-white shrink-0"
                                        style={{ background: "linear-gradient(to right, #ec4899, #3b82f6)" }}
                                    >
                                        Connect mailbox
                                    </button>
                                </div>
                            )}

                            <div className="max-w-6xl w-full mx-auto flex flex-col gap-0">

                                {/* Step 1 — Screening Form preview card (opens sub-modal to edit) */}
                                {includeScreening && (
                                    <div ref={screeningRef} className="mb-5 scroll-mt-4">
                                        <div className="flex items-center gap-2 mb-3">
                                            <span className="w-6 h-6 rounded-full bg-purple-100 text-purple-700 dark:text-purple-300 text-xs font-semibold flex items-center justify-center shrink-0">1</span>
                                            <h3 className="text-md font-semibold text-gray-800 dark:text-white">Screening Form</h3>
                                        </div>
                                        <div className="bg-purple-50/40 border border-purple-100 dark:border-purple-500/30 rounded-xl px-4 py-4">
                                            {questions.length === 0 ? (
                                                <p className="text-xs text-gray-400 dark:text-gray-500 mb-3">No questions added yet.</p>
                                            ) : (
                                                <div className="rounded-xl p-2 mb-3 flex flex-col gap-2 max-h-64 overflow-y-auto bg-gray-50/40 border border-gray-100 dark:border-gray-700/50">
                                                    {questions.map((q, i) => (
                                                        <div key={q.key || i} className="border border-gray-200 dark:border-gray-600 rounded-xl bg-white dark:bg-white/5">
                                                            <QuestionSummaryRow q={q} currency={currency} />
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                            <button
                                                type="button"
                                                onClick={openFormBuilder}
                                                className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium border border-purple-200 dark:border-purple-500/30 text-purple-700 dark:text-purple-300 bg-white dark:bg-white/5 hover:bg-purple-50"
                                            >
                                                {questions.length === 0 ? "Build form" : "Edit form"} <ArrowRight className="w-3.5 h-3.5" />
                                            </button>
                                        </div>
                                    </div>
                                )}

                                {/* Step 2 — Sequence header (screening only) */}
                                {includeScreening && (
                                    <div className="flex items-center gap-2 mb-3 mt-2">
                                        <span className="w-6 h-6 rounded-full bg-purple-100 text-purple-700 dark:text-purple-300 text-xs font-semibold flex items-center justify-center shrink-0">2</span>
                                        <h3 className="text-md font-semibold text-gray-800 dark:text-white">Outreach</h3>
                                    </div>
                                )}

                                {/* Start timing — styled like wait connector pill */}
                                <div className="flex flex-col items-center gap-1 shrink-0 mb-1">
                                    <div className="flex items-center gap-2 px-3 py-1.5 bg-gray-50 dark:bg-white/[0.03] rounded-full border border-gray-200 dark:border-gray-600">
                                        <span className="text-xs text-gray-400 dark:text-gray-500">Start</span>
                                        <div className="relative">
                                            <select
                                                value={startTiming}
                                                onChange={e => { setDirty(true); setStartTiming(e.target.value); }}
                                                className="appearance-none text-xs text-gray-600 dark:text-gray-300 bg-white dark:bg-gray-900 rounded-md outline-none cursor-pointer pr-5"
                                            >
                                                <option value="immediately" className="bg-white dark:bg-white/5 text-gray-700 dark:text-gray-300">Immediately</option>
                                                <option value="next_business_day" className="bg-white dark:bg-white/5 text-gray-700 dark:text-gray-300">Next day</option>
                                            </select>
                                            <ChevronDown className="w-3 h-3 text-gray-400 dark:text-gray-500 absolute right-0 top-1/2 -translate-y-1/2 pointer-events-none" />
                                        </div>
                                    </div>
                                    <div className="w-px h-3 bg-gray-200" />
                                    <svg className="w-2.5 h-2.5 text-gray-300 dark:text-gray-600" viewBox="0 0 10 10" fill="currentColor">
                                        <polygon points="5,10 0,0 10,0" />
                                    </svg>
                                </div>

                                {steps.map((step, idx) => (
                                    <React.Fragment key={idx}>

                                        {/* Wait connector before steps 2, 3, … */}
                                        {idx > 0 && (
                                            <div className="flex flex-col items-center gap-1 py-1 shrink-0">
                                                <div className="w-px h-4 bg-gray-200" />
                                                <div className="flex items-center gap-2 px-3 py-1.5 bg-gray-50 dark:bg-white/[0.03] rounded-full border border-gray-200 dark:border-gray-600">
                                                    <span className="text-xs text-gray-400 dark:text-gray-500">Wait</span>
                                                    <input
                                                        type="number"
                                                        min={idx === 1 ? 1 : steps[idx - 1].delayDays + 1}
                                                        value={step.delayDays}
                                                        onChange={e => updateStep(idx, "delayDays", Number(e.target.value))}
                                                        className="w-10 text-xs text-center border border-gray-200 dark:border-gray-600 rounded-md outline-none focus:border-purple-400 py-0.5"
                                                    />
                                                    <span className="text-xs text-gray-400 dark:text-gray-500">days · Send @</span>
                                                    <input
                                                        type="time"
                                                        value={step.sendAt}
                                                        onChange={e => updateStep(idx, "sendAt", e.target.value)}
                                                        className="text-xs border border-gray-200 dark:border-gray-600 rounded-md px-1.5 py-0.5 outline-none focus:border-purple-400"
                                                    />
                                                </div>
                                                <div className="w-px h-3 bg-gray-200" />
                                                <svg className="w-2.5 h-2.5 text-gray-300 dark:text-gray-600" viewBox="0 0 10 10" fill="currentColor">
                                                    <polygon points="5,10 0,0 10,0" />
                                                </svg>
                                            </div>
                                        )}

                                        {/* Step card */}
                                        <div
                                            ref={el => { stepRefs.current[idx] = el; }}
                                            className="bg-white dark:bg-white/5 border border-gray-200 dark:border-gray-600 rounded-xl overflow-hidden shrink-0 mb-2"
                                        >
                                            {/* Card header */}
                                            <div className="flex items-center gap-3 px-4 py-2.5 bg-gray-50 dark:bg-white/[0.03] border-b border-gray-100 dark:border-gray-700/50">
                                                <span className="text-xs font-semibold text-gray-700 dark:text-gray-300">{includeScreening ? `Step 2.${idx + 1}` : `Step ${idx + 1}`}</span>
                                                <div className="flex-1" />
                                                {hasEmail(step.channel) && (
                                                    <button
                                                        onClick={() => setPreviewStep(step)}
                                                        className="text-xs text-gray-500 dark:text-gray-400 border border-gray-200 dark:border-gray-600 rounded-lg px-2.5 py-1 hover:bg-gray-100 dark:hover:bg-white/10"
                                                    >
                                                        Preview and test
                                                    </button>
                                                )}
                                                {steps.length > 1 && (
                                                    <button
                                                        onClick={() => deleteStep(idx)}
                                                        className="text-gray-300 dark:text-gray-600 hover:text-red-400 transition-all"
                                                    >
                                                        <Trash2 className="w-4 h-4" />
                                                    </button>
                                                )}
                                            </div>

                                            {/* Card body */}
                                            <div className="px-5 py-4 flex flex-col gap-3">

                                                {/* Channel selector */}
                                                <div className="flex items-center gap-2">
                                                    <span className="text-xs text-gray-400 dark:text-gray-500 w-14 shrink-0">Channel</span>
                                                    <div className="flex items-center gap-1">
                                                        {channelOptions.map(c => (
                                                            <button
                                                                key={c.value}
                                                                onClick={() => updateStep(idx, "channel", c.value)}
                                                                className={`px-3 py-1 rounded-full text-xs font-medium border transition-all ${step.channel === c.value
                                                                        ? "border-purple-400 bg-purple-50 text-purple-700"
                                                                        : "border-gray-200 text-gray-600 hover:border-gray-300"
                                                                    }`}
                                                            >
                                                                {c.label}
                                                            </button>
                                                        ))}
                                                    </div>
                                                </div>

                                                {hasEmail(step.channel) && (
                                                    <>
                                                        {/* From */}
                                                        <div className="flex items-center gap-3 border-b border-gray-100 dark:border-gray-700/50 pb-3">
                                                            <span className="text-xs text-gray-400 dark:text-gray-500 w-14 shrink-0">From</span>
                                                            <span className="text-sm text-gray-700 dark:text-gray-300">
                                                                {gmailEmail || "No email connected"}
                                                            </span>
                                                        </div>

                                                        {/* Subject */}
                                                        <div className="border-b border-gray-100 dark:border-gray-700/50 pb-3">
                                                            <div className="flex items-center gap-2">
                                                                <span className="text-xs text-gray-400 dark:text-gray-500 w-14 shrink-0">Subject</span>
                                                                <input
                                                                    ref={el => { subjectRefs.current[idx] = el; }}
                                                                    type="text"
                                                                    value={step.subject}
                                                                    onChange={e => updateStep(idx, "subject", e.target.value)}
                                                                    onFocus={() => {
                                                                        focusedStepIdxRef.current = idx;
                                                                        focusedFieldRef.current = "subject";
                                                                    }}
                                                                    className="flex-1 text-sm text-gray-700 dark:text-gray-300 outline-none bg-transparent"
                                                                    placeholder="Email subject line"
                                                                />
                                                            </div>
                                                        </div>

                                                        {/* Variable chips */}
                                                        <div className="flex flex-wrap gap-1.5">
                                                            {VARIABLES.map(v => (
                                                                <button
                                                                    key={v.token}
                                                                    onClick={() => insertSnippet(idx, v.token)}
                                                                    className="text-xs px-2.5 py-1 rounded-full border border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:border-purple-400 hover:text-purple-600 hover:bg-purple-50 transition-all"
                                                                >
                                                                    {v.label}
                                                                </button>
                                                            ))}
                                                        </div>

                                                        {/* Body */}
                                                        <EmailBodyInput
                                                            ref={el => { bodyRefs.current[idx] = el; }}
                                                            value={step.body}
                                                            onChange={val => updateStep(idx, "body", val)}
                                                            placeholder="Email body content"
                                                        />
                                                    </>
                                                )}

                                                {hasWhatsApp(step.channel) && (
                                                    <div className={hasEmail(step.channel) ? "border-t border-gray-100 pt-3 flex flex-col gap-2" : "flex flex-col gap-2"}>
                                                        <span className="text-xs text-green-600 dark:text-green-400 font-medium flex items-center gap-1.5">
                                                            <img src={whatsappLogo} alt="" className="w-3.5 h-3.5" />
                                                            WhatsApp template
                                                        </span>
                                                        {waTemplates.length === 0 ? (
                                                            <p className="text-xs text-gray-400 dark:text-gray-500">
                                                                No WhatsApp templates registered yet. Ask a developer to register one from the Entry Point.
                                                            </p>
                                                        ) : (
                                                            <select
                                                                value={step.whatsappTemplateId || ""}
                                                                onChange={e => updateStep(idx, "whatsappTemplateId", e.target.value || null)}
                                                                className="w-full border border-gray-200 dark:border-gray-600 rounded-lg px-3 py-2 text-sm outline-none focus:border-green-400 bg-white dark:bg-gray-900"
                                                            >
                                                                <option value="">Select a template…</option>
                                                                {waTemplates.map(t => (
                                                                    <option key={t.id} value={t.id}>{t.name}</option>
                                                                ))}
                                                            </select>
                                                        )}
                                                        {step.whatsappTemplateId && (
                                                            <div className="bg-[#e5ddd5] rounded-xl px-3 py-3 flex justify-start">
                                                                <div className="bg-[#d9fdd3] rounded-lg rounded-tl-none px-3 py-2 text-sm text-gray-800 dark:text-white max-w-[85%] shadow-sm whitespace-pre-wrap">
                                                                    {renderWaPreview(waTemplates.find(t => t.id === step.whatsappTemplateId))}
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>
                                                )}

                                                {hasCall(step.channel) && (
                                                    <div className="flex items-start gap-2 bg-blue-50/60 border border-blue-100 dark:border-blue-500/30 rounded-lg px-3 py-3">
                                                        <span className="text-base leading-none">☎</span>
                                                        <p className="text-xs text-blue-800 leading-snug">
                                                            An AI voice agent calls the candidate and asks the screening questions above,
                                                            using the job context. Nothing to write here — the candidate needs a phone number.
                                                        </p>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </React.Fragment>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {showFormBuilder && (
                <div className="fixed inset-0 z-[100] bg-black/20 backdrop-blur-sm flex items-center justify-center">
                    <div className="w-[96vw] max-w-7xl h-[90vh] bg-white dark:bg-[#1B1524] rounded-2xl flex flex-col overflow-hidden shadow-2xl">
                        <div className="bg-white dark:bg-white/5 border-b border-gray-100 dark:border-gray-700/50 px-6 py-4 flex items-center gap-3 shrink-0">
                            <span className="text-sm font-semibold text-gray-800 dark:text-white">Screening Form</span>
                            <span className="text-xs text-gray-400 dark:text-gray-500 truncate flex-1">· {seqName}</span>
                            <button
                                type="button"
                                onClick={cancelFormBuilder}
                                className="px-3 py-1.5 text-sm border border-gray-200 dark:border-gray-600 rounded-full text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-white/5 mr-1"
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                onClick={saveFormBuilder}
                                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium text-white"
                                style={{ background: "linear-gradient(to right, #ec4899, #3b82f6)" }}
                            >
                                Save & return
                            </button>
                        </div>
                        <div className="flex-1 flex overflow-hidden">
                            <QuestionsBuilder
                                questions={questions}
                                onChange={(q) => { setDirty(true); setQuestions(q); }}
                                currency={currency}
                                onCurrencyChange={setCurrency}
                            />
                        </div>
                    </div>
                </div>
            )}

            <EmailTestModal
                isOpen={!!previewStep}
                onClose={() => setPreviewStep(null)}
                step={previewStep}
                gmailEmail={gmailEmail}
                orgId={orgId}
                recruiterId={recruiterId}
            />
        </>
    );
};

export default SequenceCreatorModal;